import { Events, FSMCommonTransitions } from '../../constants'
import { has, get } from 'lodash'
import { StatesConfigType } from '../../config'

const {
  FNOL_CLAIMS,
  FNOL_CLAIMS_LIST,
  FNOL_COLLISION_FLOW,
  FNOL_COLLISION_WHAT_HAPPENED,
  FNOL_COLLISION_HOW_MANY,
  FNOL_COLLISION_ANIMAL,
  FNOL_COLLISION_OBJECT,
  FNOL_COLLISION_RESPONSIBLE,
  FNOL_COLLISION_INJURED,
  FNOL_COLLISION_CASE_OF_ACCIDENT,
  FNOL_COLLISION_REGISTRATION_NUM,
  FNOL_COLLISION_WHEN_WHERE,
  FNOL_COLLISION_ACCIDENT_FORM,
  FNOL_COLLISION_DAMAGE_REPORT,
  FNOL_ADDITIONAL_INFORMATION,
  FNOL_COLLISION_MORE_DETAILS,
  FNOL_TIME_LINE,
  MY_PAGES,
  HISTORY,
} = FSMCommonTransitions

export default (FLOW_MACHINE_ID: string): StatesConfigType => ({
  [FNOL_COLLISION_FLOW]: {
    initial: FNOL_COLLISION_WHAT_HAPPENED,
    states: {
      [FNOL_COLLISION_WHAT_HAPPENED]: {
        on: {
          [Events.BACK]: `#${FLOW_MACHINE_ID}.${FNOL_CLAIMS}.${FNOL_CLAIMS_LIST}`,
          [Events.FNOL_COLLISION_HOW_MANY]: FNOL_COLLISION_HOW_MANY,
          [Events.FNOL_COLLISION_ANIMAL]: FNOL_COLLISION_ANIMAL,
          [Events.FNOL_COLLISION_OBJECT]: FNOL_COLLISION_OBJECT,
          [Events.FNOL_COLLISION_INJURED]: {
            target: FNOL_COLLISION_INJURED,
            actions: 'setBackPath',
          },
        },
      },
      [FNOL_COLLISION_HOW_MANY]: {
        on: {
          [Events.BACK]: FNOL_COLLISION_WHAT_HAPPENED,
          [Events.FNOL_COLLISION_RESPONSIBLE]: FNOL_COLLISION_RESPONSIBLE,
        },
      },
      [FNOL_COLLISION_OBJECT]: {
        on: {
          [Events.BACK]: FNOL_COLLISION_WHAT_HAPPENED,
          [Events.NEXT]: { target: FNOL_COLLISION_INJURED, actions: 'setBackPath' },
        },
      },
      [FNOL_COLLISION_ANIMAL]: {
        on: {
          [Events.BACK]: FNOL_COLLISION_WHAT_HAPPENED,
          [Events.NEXT]: { target: FNOL_COLLISION_INJURED, actions: 'setBackPath' },
        },
      },
      [FNOL_COLLISION_RESPONSIBLE]: {
        on: {
          [Events.BACK]: FNOL_COLLISION_HOW_MANY,
          [Events.FNOL_COLLISION_INJURED]: {
            target: FNOL_COLLISION_INJURED,
            actions: 'setBackPath',
          },
        },
      },
      [FNOL_COLLISION_INJURED]: {
        on: {
          [Events.BACK]: [
            {
              target: FNOL_COLLISION_WHAT_HAPPENED,
              guard: ({ context }) =>
                has(context.backPath, FNOL_COLLISION_FLOW) &&
                get(context.backPath, FNOL_COLLISION_FLOW) === FNOL_COLLISION_WHAT_HAPPENED,
            },
            {
              target: `#${FLOW_MACHINE_ID}.${FNOL_COLLISION_FLOW}.${FNOL_COLLISION_ANIMAL}`,
              guard: ({ context }) =>
                has(context.backPath, FNOL_COLLISION_FLOW) &&
                get(context.backPath, FNOL_COLLISION_FLOW) === FNOL_COLLISION_ANIMAL,
            },
            {
              target: `#${FLOW_MACHINE_ID}.${FNOL_COLLISION_FLOW}.${FNOL_COLLISION_RESPONSIBLE}`,
              guard: ({ context }) =>
                [
                  FNOL_COLLISION_RESPONSIBLE,
                  FNOL_COLLISION_CASE_OF_ACCIDENT,
                  FNOL_COLLISION_ACCIDENT_FORM,
                  FNOL_COLLISION_INJURED,
                ].includes(get(context.backPath, FNOL_COLLISION_FLOW)),
            },
            {
              target: `#${FLOW_MACHINE_ID}.${FNOL_COLLISION_FLOW}.${FNOL_COLLISION_OBJECT}`,
              guard: ({ context }) =>
                has(context.backPath, FNOL_COLLISION_FLOW) &&
                get(context.backPath, FNOL_COLLISION_FLOW) === FNOL_COLLISION_OBJECT,
            },
            {
              target: `#${FLOW_MACHINE_ID}.${FNOL_COLLISION_FLOW}.${FNOL_COLLISION_RESPONSIBLE}`,
              guard: ({ context }) =>
                has(context.backPath, FNOL_COLLISION_FLOW) &&
                get(context.backPath, FNOL_COLLISION_FLOW) === FNOL_COLLISION_DAMAGE_REPORT,
            },
            {
              target: `#${FLOW_MACHINE_ID}.${FNOL_COLLISION_FLOW}.${HISTORY}`,
              guard: ({ context }) =>
                has(context.backPath, FNOL_COLLISION_FLOW) &&
                get(context.backPath, FNOL_COLLISION_FLOW) === FNOL_COLLISION_MORE_DETAILS,
            },
            // Default back to FNOL_COLLISION_WHAT_HAPPENED state
            {
              target: FNOL_COLLISION_WHAT_HAPPENED,
            },
          ],
          [Events.NEXT]: [
            {
              target: FNOL_COLLISION_CASE_OF_ACCIDENT,
              guard: ({ context }) =>
                [
                  FNOL_COLLISION_RESPONSIBLE,
                  FNOL_COLLISION_CASE_OF_ACCIDENT,
                  FNOL_COLLISION_INJURED,
                  FNOL_COLLISION_DAMAGE_REPORT,
                ].includes(get(context.backPath, FNOL_COLLISION_FLOW)),
              actions: 'setBackPath',
            },
            {
              // NB: this was `action: 'setBackPath'` (singular) — a key xstate v4 silently
              // ignored, so `setBackPath` never ran here. Removed rather than corrected to
              // `actions:`, which would be a behaviour change. See DFE-3898 notes.
              target: FNOL_COLLISION_MORE_DETAILS,
            },
          ],
        },
      },
      [FNOL_COLLISION_CASE_OF_ACCIDENT]: {
        on: {
          [Events.BACK]: {
            target: FNOL_COLLISION_INJURED,
          },
          [Events.FNOL_COLLISION_REGISTRATION_NUM]: {
            target: FNOL_COLLISION_REGISTRATION_NUM,
            actions: 'setBackPath',
          },
        },
      },
      [FNOL_COLLISION_REGISTRATION_NUM]: {
        on: {
          [Events.BACK]: FNOL_COLLISION_CASE_OF_ACCIDENT,
          // NB: was `action:` (singular) — silently ignored by v4, so `setBackPath` never ran.
          [Events.NEXT]: { target: FNOL_COLLISION_WHEN_WHERE },
        },
      },
      [FNOL_COLLISION_WHEN_WHERE]: {
        on: {
          [Events.BACK]: [
            {
              target: `#${FLOW_MACHINE_ID}.${FNOL_COLLISION_FLOW}.${FNOL_COLLISION_REGISTRATION_NUM}`,
              guard: ({ context }) =>
                (has(context.backPath, FNOL_COLLISION_FLOW) &&
                  get(context.backPath, FNOL_COLLISION_FLOW) === FNOL_COLLISION_CASE_OF_ACCIDENT) ||
                get(context.backPath, FNOL_COLLISION_FLOW) === FNOL_COLLISION_WHEN_WHERE,
            },
            // Case when user navigates back from FNOL_COLLISION_ACCIDENT_FORM
            {
              target: `#${FLOW_MACHINE_ID}.${FNOL_COLLISION_FLOW}.${FNOL_COLLISION_MORE_DETAILS}`,
              guard: ({ context }) =>
                has(context.backPath, FNOL_COLLISION_FLOW) &&
                get(context.backPath, FNOL_COLLISION_FLOW) === FNOL_COLLISION_WHAT_HAPPENED,
            },
            {
              target: `#${FLOW_MACHINE_ID}.${FNOL_COLLISION_FLOW}.${FNOL_COLLISION_REGISTRATION_NUM}`,
              guard: ({ context }) =>
                has(context.backPath, FNOL_COLLISION_FLOW) &&
                get(context.backPath, FNOL_COLLISION_FLOW) === FNOL_COLLISION_ACCIDENT_FORM,
            },
            {
              target: `#${FLOW_MACHINE_ID}.${FNOL_COLLISION_FLOW}.${FNOL_COLLISION_REGISTRATION_NUM}`,
              guard: ({ context }) =>
                has(context.backPath, FNOL_COLLISION_FLOW) &&
                get(context.backPath, FNOL_COLLISION_FLOW) === FNOL_COLLISION_DAMAGE_REPORT,
            },
            // Default Back
            {
              target: `#${FLOW_MACHINE_ID}.${FNOL_COLLISION_FLOW}.${FNOL_COLLISION_MORE_DETAILS}`,
              actions: 'setBackPath',
            },
          ],
          [Events.NEXT]: [
            {
              target: `#${FLOW_MACHINE_ID}.${FNOL_COLLISION_FLOW}.${FNOL_COLLISION_ACCIDENT_FORM}`,
              guard: ({ context }) =>
                has(context.backPath, FNOL_COLLISION_FLOW) &&
                get(context.backPath, FNOL_COLLISION_FLOW) === FNOL_COLLISION_CASE_OF_ACCIDENT,
            },
            // Default Next
            {
              target: `#${FLOW_MACHINE_ID}.${FNOL_COLLISION_FLOW}.${FNOL_ADDITIONAL_INFORMATION}`,
              actions: 'setBackPath',
            },
          ],
        },
      },
      [FNOL_COLLISION_ACCIDENT_FORM]: {
        on: {
          [Events.BACK]: FNOL_COLLISION_WHEN_WHERE,
          [Events.NEXT]: { target: FNOL_COLLISION_DAMAGE_REPORT, actions: 'setBackPath' },
          [Events.FNOL_ADDITIONAL_INFORMATION]: {
            target: FNOL_ADDITIONAL_INFORMATION,
            actions: 'setBackPath',
          },
        },
      },
      [FNOL_COLLISION_DAMAGE_REPORT]: {
        on: {
          [Events.BACK]: FNOL_COLLISION_ACCIDENT_FORM,
          [Events.NEXT]: { target: FNOL_ADDITIONAL_INFORMATION, actions: 'setBackPath' },
        },
      },
      [FNOL_ADDITIONAL_INFORMATION]: {
        on: {
          [Events.BACK]: [
            {
              target: `#${FLOW_MACHINE_ID}.${FNOL_COLLISION_FLOW}.${FNOL_COLLISION_DAMAGE_REPORT}`,
              guard: ({ context }) =>
                has(context.backPath, FNOL_COLLISION_FLOW) &&
                get(context.backPath, FNOL_COLLISION_FLOW) === FNOL_COLLISION_DAMAGE_REPORT,
            },
            {
              target: `#${FLOW_MACHINE_ID}.${FNOL_COLLISION_FLOW}.${FNOL_COLLISION_ACCIDENT_FORM}`,
              guard: ({ context }) =>
                has(context.backPath, FNOL_COLLISION_FLOW) &&
                get(context.backPath, FNOL_COLLISION_FLOW) === FNOL_COLLISION_ACCIDENT_FORM,
            },
            // Default Back
            {
              target: `#${FLOW_MACHINE_ID}.${FNOL_COLLISION_FLOW}.${FNOL_COLLISION_WHEN_WHERE}`,
            },
          ],
          [Events.NEXT]: FNOL_TIME_LINE,
        },
      },
      [FNOL_COLLISION_MORE_DETAILS]: {
        on: {
          [Events.BACK]: {
            target: `#${FLOW_MACHINE_ID}.${FNOL_COLLISION_FLOW}.${FNOL_COLLISION_INJURED}`,
          },
          [Events.NEXT]: FNOL_COLLISION_WHEN_WHERE,
        },
      },
      [FNOL_TIME_LINE]: {
        on: {
          [Events.BACK]: `#${FLOW_MACHINE_ID}.${MY_PAGES}`,
          [Events.NEXT]: `#${FLOW_MACHINE_ID}.${MY_PAGES}`,
        },
      },
      [HISTORY]: {
        type: 'history',
        history: 'deep',
      },
    },
  },
})
